
import { useEffect, useState } from "react";
import { SETS, QUESTIONS, EMPTY_TEAM, statusOf, teamInfo, myPicks, pad } from "./qa";
import Sidebar from "./components/Sidebar/Sidebar";
import Topbar from "./components/Topbar/Topbar";
import StatsCard from "./components/StatsCard/StatsCard";
import SetIndex from "./components/SetIndex/SetIndex";
import Filters from "./components/Filters/Filters";
import QuestionCard from "./components/QuestionCard/QuestionCard";
import SetSummary from "./components/SetSummary/SetSummary";
import AnalyticsPanel from "./components/AnalyticsPanel/AnalyticsPanel";
import ReviewerGate from "./components/ReviewerGate/ReviewerGate";
import { fetchState, sendAction } from "./api";
import styles from "./QAApp.module.css";

const REVIEWER_KEY = "nssc-qa-reviewer";
const POLL_MS = 6000;

const MATCHERS = {
  all: () => true,
  easy: (q) => q.difficulty === "easy",
  medium: (q) => q.difficulty === "medium",
  hard: (q) => q.difficulty === "hard",
  verified: (q, s) => s !== "unverified",
  unverified: (q, s) => s === "unverified",
  errors: (q, s) => s === "incorrect",
};

const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);

function scrollToId(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function QA() {
  const [ready, setReady] = useState(false);
  const [reviewer, setReviewer] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [team, setTeam] = useState(EMPTY_TEAM);
  const [online, setOnline] = useState(true);
  const [saveError, setSaveError] = useState("");
  const [setId, setSetId] = useState(1);
  const [filter, setFilter] = useState("all");
  const [showKeys, setShowKeys] = useState(false);
  const [focusId, setFocusId] = useState(null);

  useEffect(() => {
    queueMicrotask(() => {
      try {
        setReviewer(localStorage.getItem(REVIEWER_KEY));
      } catch {}
      setReady(true);
    });
  }, []);

  useEffect(() => {
    let live = true;
    async function load() {
      try {
        const data = await fetchState();
        if (live) {
          setTeam(data);
          setOnline(true);
        }
      } catch {
        if (live) setOnline(false);
      }
    }
    load();
    const timer = setInterval(load, POLL_MS);
    const onVisible = () => {
      if (!document.hidden) load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      live = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  useEffect(() => {
    if (focusId) scrollToId(`q-${focusId}`);
  }, [focusId, setId]);

  async function send(payload) {
    try {
      setTeam(await sendAction({ reviewer, ...payload }));
      setOnline(true);
      setSaveError("");
    } catch (err) {
      setOnline(false);
      setSaveError(err.fromServer ? err.message : "No connection");
    }
  }

  function saveReviewer(name) {
    try {
      localStorage.setItem(REVIEWER_KEY, name);
    } catch {}
    setReviewer(name);
    setSwitching(false);
  }

  if (!ready) return null;

  const picks = reviewer ? myPicks(team, reviewer) : {};
  const statuses = {};
  const infos = {};
  const teamStatuses = {};
  for (const q of QUESTIONS) {
    statuses[q.id] = statusOf(q, picks);
    infos[q.id] = teamInfo(q, team);
    teamStatuses[q.id] = infos[q.id].status;
  }

  const set = SETS[setId - 1];
  const matcher = MATCHERS[filter];
  const visible = set.questions.filter((q) => matcher(q, teamStatuses[q.id]));

  const checkedCount = QUESTIONS.filter((q) => infos[q.id].checked).length;
  const flaggedCount = QUESTIONS.filter((q) => infos[q.id].flagged).length;

  const marks = {};
  const dimmed = {};
  const reviewers = {};
  const signersOf = {};
  let checkedSets = 0;
  for (const s of SETS) {
    const entry = team.sets[s.id] ?? {};
    const signers = Object.keys(entry.signoffs ?? {});
    signersOf[s.id] = signers;
    if (signers.length) checkedSets += 1;
    const first = signers[0] ?? entry.claimedBy;
    if (first) reviewers[s.id] = signers.length > 1 ? `${first} +${signers.length - 1}` : first;
    if (s.questions.some((q) => infos[q.id].flagged)) marks[s.id] = "warn";
    else if (signers.length) marks[s.id] = "ok";
    else if (entry.claimedBy) marks[s.id] = "wip";
    if (filter !== "all" && !s.questions.some((q) => matcher(q, teamStatuses[q.id]))) {
      dimmed[s.id] = true;
    }
  }

  const entry = team.sets[set.id] ?? {};
  const signers = signersOf[set.id];
  const iSigned = signers.includes(reviewer);
  const iClaimed = entry.claimedBy === reviewer;

  const myAnswered = set.questions.filter((q) => statuses[q.id] !== "unverified").length;
  const myScore = set.questions.reduce(
    (sum, q) => sum + (statuses[q.id] === "correct" ? q.points : 0),
    0,
  );

  const distribution = [0, 0, 0, 0];
  for (const q of QUESTIONS) distribution[q.answerIndex] += 1;

  function goSet(id) {
    setFocusId(null);
    setSetId(id);
    requestAnimationFrame(() => scrollToId("qa-questions"));
  }

  function firstFreeSet() {
    const free =
      SETS.find((s) => !signersOf[s.id].length && !team.sets[s.id]?.claimedBy) ??
      SETS.find((s) => !signersOf[s.id].length);
    if (free) goSet(free.id);
  }

  function openQuestion(q) {
    setFilter("all");
    setSetId(q.set);
    setFocusId(q.id);
  }

  function choose(qid, index) {
    if (picks[qid] !== undefined) return;
    setTeam((prev) => ({
      ...prev,
      checks: { ...prev.checks, [qid]: { ...prev.checks[qid], [reviewer]: { pick: index, ts: Date.now() } } },
    }));
    send({ action: "check", qid, pick: index });
    if (!entry.claimedBy && !signers.length) send({ action: "claim", setId: set.id, on: true });
  }

  function resetMine() {
    if (!window.confirm(`Reset all your checks for set ${pad(set.id)}?`)) return;
    send({ action: "reset", setId: set.id });
  }

  function exportResults() {
    const report = {
      exportedAt: new Date().toISOString(),
      sets: SETS.map((s) => ({
        set: s.id,
        claimedBy: team.sets[s.id]?.claimedBy ?? null,
        checkedBy: signersOf[s.id],
      })),
      questions: QUESTIONS.map((q) => ({
        id: q.id,
        question: q.text,
        answer: q.options[q.answerIndex],
        checks: Object.entries(team.checks[q.id] ?? {}).map(([name, c]) => ({
          reviewer: name,
          picked: q.options[c.pick],
          correct: c.pick === q.answerIndex,
        })),
        notes: team.notes[q.id] ?? [],
      })),
    };
    const url = URL.createObjectURL(new Blob([JSON.stringify(report, null, 2)], { type: "application/json" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "nssc-treasure-hunt-qa.json";
    link.click();
    URL.revokeObjectURL(url);
  }

  function onNav(id) {
    if (id === "overview") {
      setFilter("all");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (id === "bank") scrollToId("qa-questions");
    else if (id === "sets") scrollToId("qa-sets");
    else if (id === "keys") setShowKeys((v) => !v);
    else if (id === "errors") {
      setFilter("errors");
      const bad = SETS.find((s) => s.questions.some((q) => infos[q.id].flagged));
      if (bad) goSet(bad.id);
    }
  }

  const prevSet = setId > 1 ? setId - 1 : null;
  const nextSet = setId < SETS.length ? setId + 1 : null;

  return (
    <div className={styles.app}>
      {(!reviewer || switching) && <ReviewerGate initial={reviewer ?? ""} onSubmit={saveReviewer} />}
      {saveError && (
        <p className={styles.banner} role="alert">
          Save failed: {saveError}
        </p>
      )}

      <Sidebar active={filter === "errors" ? "errors" : "bank"} errorCount={flaggedCount} onNav={onNav} />

      <div className={styles.main}>
        <Topbar
          reviewer={reviewer ?? "?"}
          online={online}
          showKeys={showKeys}
          onPick={openQuestion}
          onFilter={() => scrollToId("qa-filters")}
          onSets={() => scrollToId("qa-sets")}
          onExport={exportResults}
          onToggleKeys={() => setShowKeys((v) => !v)}
          onSwitch={() => setSwitching(true)}
        />

        <div className={styles.body}>
          <div className={styles.column}>
            <header className={styles.header}>
              <div>
                <h1 className={styles.title}>
                  Question
                  <br />
                  Verification
                </h1>
                <p className={styles.meta}>
                  <span>{QUESTIONS.length} Questions</span>
                  <span>{SETS.length} Sets</span>
                  <span>{set.questions.length} Questions / Set</span>
                </p>
              </div>
              <div className={styles.pager}>
                <button type="button" className={styles.pagerBtn} disabled={!prevSet} onClick={() => goSet(prevSet)}>
                  ← Previous set
                </button>
                <button type="button" className={styles.pagerBtn} disabled={!nextSet} onClick={() => goSet(nextSet)}>
                  Next set →
                </button>
              </div>
            </header>

            <section className={styles.stats} aria-label="Team progress">
              <StatsCard accent label="Total questions" value={QUESTIONS.length} delta={`${SETS.length} sets`} ghost="#" />
              <StatsCard label="Sets checked" value={checkedSets} delta={`${pct(checkedSets, SETS.length)}%`} ghost="◎" />
              <StatsCard label="Questions checked" value={checkedCount} delta={`${pct(checkedCount, QUESTIONS.length)}%`} ghost="✓" />
              <StatsCard label="Flagged" value={flaggedCount} delta={`${pct(flaggedCount, checkedCount)}%`} ghost="×" />
            </section>

            <SetIndex
              sets={SETS}
              current={setId}
              marks={marks}
              dimmed={dimmed}
              reviewers={reviewers}
              onSelect={goSet}
              onFirstFree={firstFreeSet}
            />

            <Filters value={filter} onChange={setFilter} />

            <section id="qa-questions" className={styles.questions} aria-labelledby="qa-set-title">
              <h2 id="qa-set-title" className={styles.setTitle}>
                <span>Set {pad(set.id)}</span>
                <span className={styles.progression}>
                  {signers.length ? `Reviewed by ${signers.join(", ")}` : entry.claimedBy ? `In review: ${entry.claimedBy}` : "Not reviewed"}
                </span>
                <span className={styles.progression}>Easy → Medium → Hard</span>
              </h2>
              {visible.map((q) => (
                <QuestionCard
                  key={q.id}
                  question={q}
                  pick={picks[q.id]}
                  status={statuses[q.id]}
                  team={infos[q.id]}
                  showKey={showKeys}
                  focused={focusId === q.id}
                  reviewer={reviewer}
                  onPick={choose}
                  onClear={(qid) => send({ action: "uncheck", qid })}
                  onComment={(qid, text) => send({ action: "comment", qid, text })}
                  onSuggest={(qid, suggestion, text) => send({ action: "suggest", qid, suggestion, text })}
                  onDeleteNote={(qid, id) => send({ action: "delnote", qid, id })}
                />
              ))}
              {visible.length === 0 && (
                <p className={styles.empty}>No {filter} questions in set {pad(set.id)}.</p>
              )}
            </section>

            <SetSummary
              set={set}
              statuses={statuses}
              teamStatuses={teamStatuses}
              score={myScore}
              answered={myAnswered}
              claimedBy={entry.claimedBy}
              signers={signers}
              iClaimed={iClaimed}
              iSigned={iSigned}
              onClaim={() => send({ action: "claim", setId: set.id, on: !iClaimed })}
              onSign={() => send({ action: "signoff", setId: set.id, on: !iSigned })}
              onReset={resetMine}
            />

            <nav className={styles.bottomNav} aria-label="Set navigation">
              <button type="button" className={styles.navBtn} disabled={!prevSet} onClick={() => goSet(prevSet)}>
                ← Previous set
              </button>
              <p className={styles.navCurrent}>
                Set {pad(set.id)} / {SETS.length}
              </p>
              <button type="button" className={styles.navBtn} disabled={!nextSet} onClick={() => goSet(nextSet)}>
                Next set →
              </button>
            </nav>
          </div>

          <AnalyticsPanel set={set} statuses={teamStatuses} distribution={distribution} />
        </div>
      </div>
    </div>
  );
}
